import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Pagination,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import type { AdminSkill } from '@prepzio/shared';
import { IconEdit, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';

import { SkillPicker } from '../components/skill-picker';
import { useCreateSkill, useDeleteSkill, useSkills, useUpdateSkill } from '../hooks/use-content';
import { useT, type MessageKey } from '../i18n';
import { errorMessage } from '../lib/ui';

/** Rows per page. Fifty is what fits a screen without becoming a scroll. */
const PAGE_SIZE = 50;

/**
 * PRE-154 seeded 2,476 catalog rows beside the six a person wrote, and the two
 * are almost never wanted in the same view — so the filter leads with that
 * distinction rather than burying it. `user_generated` is small and transient
 * (it drains through the merge queue), but it is the one an operator most often
 * comes here looking for.
 */
const ORIGIN_FILTERS = [
  { value: '', labelKey: 'skills.originAll' },
  { value: 'curated', labelKey: 'skills.originCurated' },
  { value: 'stackoverflow', labelKey: 'skills.originCatalog' },
  { value: 'user_generated', labelKey: 'skills.originFromCvs' },
] as const satisfies readonly { value: string; labelKey: MessageKey }[];

const ORIGIN_BADGE: Record<AdminSkill['origin'], { labelKey: MessageKey; color: string }> = {
  curated: { labelKey: 'skills.badge.curated', color: 'blue' },
  stackoverflow: { labelKey: 'skills.badge.stackoverflow', color: 'gray' },
  user_generated: { labelKey: 'skills.badge.user_generated', color: 'yellow' },
};

/** Derive a kebab slug from a title (the operator can still edit it). */
function slugify(title: string): string {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

type Draft = {
  id: string | null;
  slug: string;
  title: string;
  /** PRE-92: parent skill id, or null for a root. Replaces the free-text category. */
  parentId: string | null;
  description: string;
};

const EMPTY: Draft = { id: null, slug: '', title: '', parentId: null, description: '' };

export function SkillsScreen() {
  const t = useT();
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 250);
  const [origin, setOrigin] = useState('');
  const [page, setPage] = useState(1);

  // A filter change with the old page number still set can land past the end of
  // the new result set, which reads as "no skills" rather than "page 7 of 1".
  useEffect(() => setPage(1), [debouncedSearch, origin]);

  const skillsQuery = useSkills({
    ...(debouncedSearch.trim() ? { q: debouncedSearch.trim() } : {}),
    ...(origin ? { origin: origin as AdminSkill['origin'] } : {}),
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });
  const create = useCreateSkill();
  const update = useUpdateSkill();
  const del = useDeleteSkill();

  const [draft, setDraft] = useState<Draft | null>(null);
  const [slugTouched, setSlugTouched] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const skills = skillsQuery.data?.items ?? [];
  const total = skillsQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const editing = draft?.id != null;

  function openCreate() {
    setDraft({ ...EMPTY });
    setSlugTouched(false);
    setFormError(null);
  }
  function openEdit(skill: AdminSkill) {
    setDraft({
      id: skill.id,
      slug: skill.slug,
      title: skill.title,
      parentId: skill.parentId,
      description: skill.description,
    });
    setSlugTouched(true);
    setFormError(null);
  }

  function save() {
    if (!draft) return;
    setFormError(null);
    const onError = (e: unknown) => setFormError(errorMessage(e));
    if (draft.id) {
      update.mutate(
        {
          id: draft.id,
          input: {
            slug: draft.slug,
            title: draft.title,
            parentId: draft.parentId,
            description: draft.description,
          },
        },
        { onSuccess: () => setDraft(null), onError },
      );
    } else {
      create.mutate(
        {
          slug: draft.slug,
          title: draft.title,
          parentId: draft.parentId,
          description: draft.description || undefined,
        },
        { onSuccess: () => setDraft(null), onError },
      );
    }
  }

  function remove(skill: AdminSkill) {
    if (skill.questionCount > 0) return; // guarded server-side too
    if (!window.confirm(t('skills.deleteConfirm', { title: skill.title }))) return;
    del.mutate(skill.id);
  }

  const saving = create.isPending || update.isPending;

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Text c="dimmed" size="sm">
          {t('skills.intro')}
        </Text>
        <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
          {t('skills.new')}
        </Button>
      </Group>

      {skillsQuery.isError && (
        <Text c="red" size="sm">
          {errorMessage(skillsQuery.error)}
        </Text>
      )}
      {del.isError && (
        <Text c="red" size="sm">
          {errorMessage(del.error)}
        </Text>
      )}

      <Group align="flex-end" gap="sm">
        <TextInput
          flex={1}
          label={t('common.search')}
          placeholder={t('skills.searchPlaceholder')}
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
        <SegmentedControl
          value={origin}
          onChange={setOrigin}
          data={ORIGIN_FILTERS.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
        />
      </Group>

      <Text c="dimmed" size="xs">
        {total === 0
          ? t('skills.noMatch')
          : t('skills.showing', { shown: skills.length, total: total.toLocaleString() })}
      </Text>

      <Card withBorder padding="lg" radius="md">
        <Table.ScrollContainer minWidth={640}>
          <Table striped highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('skills.colTitle')}</Table.Th>
                <Table.Th>{t('skills.colSlug')}</Table.Th>
                <Table.Th>{t('skills.colOrigin')}</Table.Th>
                <Table.Th>{t('skills.colParent')}</Table.Th>
                <Table.Th ta="right">{t('skills.colQuestions')}</Table.Th>
                <Table.Th ta="right">{t('common.actions')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {skills.map((skill) => (
                <Table.Tr key={skill.id}>
                  <Table.Td>
                    <Text fw={600}>{skill.title}</Text>
                    {skill.description && (
                      <Text c="dimmed" size="xs" lineClamp={2}>
                        {skill.description}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" ff="monospace">
                      {skill.slug}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={ORIGIN_BADGE[skill.origin].color} tt="none">
                      {t(ORIGIN_BADGE[skill.origin].labelKey)}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {skill.parentSlug ? (
                      <Badge variant="light" color="gray" tt="none">
                        {skill.parentSlug}
                      </Badge>
                    ) : (
                      <Text c="dimmed" size="xs">
                        {t('skills.root')}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td ta="right">{skill.questionCount}</Table.Td>
                  <Table.Td>
                    <Group gap={4} justify="flex-end" wrap="nowrap">
                      <Tooltip label={t('common.edit')} withArrow>
                        <ActionIcon variant="subtle" color="gray" onClick={() => openEdit(skill)}>
                          <IconEdit size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip
                        label={
                          skill.questionCount > 0
                            ? t('skills.deleteBlocked', { count: skill.questionCount })
                            : t('common.delete')
                        }
                        withArrow
                      >
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          disabled={skill.questionCount > 0 || del.isPending}
                          onClick={() => remove(skill)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
              {skills.length === 0 && !skillsQuery.isLoading && (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text c="dimmed" size="sm">
                      {t('skills.none')}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>

      {pageCount > 1 && (
        <Group justify="center">
          <Pagination value={page} onChange={setPage} total={pageCount} siblings={1} />
        </Group>
      )}

      <Modal
        opened={draft != null}
        onClose={() => setDraft(null)}
        title={t(editing ? 'skills.editTitle' : 'skills.newTitle')}
        centered
      >
        {draft && (
          <Stack gap="sm">
            <TextInput
              label={t('skills.title')}
              required
              value={draft.title}
              onChange={(e) => {
                const title = e.currentTarget.value;
                setDraft((d) =>
                  d ? { ...d, title, slug: !editing && !slugTouched ? slugify(title) : d.slug } : d,
                );
              }}
            />
            <TextInput
              label={t('skills.slug')}
              required
              description={t('skills.slugHint')}
              value={draft.slug}
              onChange={(e) => {
                const slug = e.currentTarget.value;
                setSlugTouched(true);
                setDraft((d) => (d ? { ...d, slug } : d));
              }}
            />
            <SkillPicker
              label={t('skills.parent')}
              description={t('skills.parentHint')}
              placeholder={t('skills.parentPlaceholder')}
              clearable
              value={draft.parentId}
              excludeIds={draft.id ? [draft.id] : []}
              selected={skills.find((s) => s.id === draft.parentId) ?? null}
              onChange={(parentId) => setDraft((d) => (d ? { ...d, parentId } : d))}
            />
            <Textarea
              label={t('skills.description')}
              autosize
              minRows={2}
              value={draft.description}
              onChange={(e) => {
                const description = e.currentTarget.value;
                setDraft((d) => (d ? { ...d, description } : d));
              }}
            />
            {formError && (
              <Text c="red" size="sm">
                {formError}
              </Text>
            )}
            <Group justify="flex-end" mt="xs">
              <Button variant="default" onClick={() => setDraft(null)}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={save}
                loading={saving}
                disabled={!draft.title.trim() || !draft.slug.trim()}
              >
                {t(editing ? 'common.save' : 'skills.create')}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
